import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DeleteLeadDialog } from "@/components/DeleteLeadDialog";

import { API } from "@/lib/api";

export const LeadDetail = ({ leadId, token, endpoint, config, onClose, onUnauthorized }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchLead = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/${endpoint}/${leadId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setForm(res.data);
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error("Sessione scaduta, effettua di nuovo l'accesso");
          onUnauthorized();
          return;
        }
        toast.error(err.response?.status === 404 ? "Scheda non trovata" : "Errore nel caricamento della scheda");
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [leadId, token, endpoint, onClose, onUnauthorized]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const toggle = (key, val) =>
    setForm((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  const inArr = (key, val) => (form[key] || []).includes(val);

  const handleSave = async () => {
    const error = config.validate(form);
    if (error) return toast.error(error);
    setSaving(true);
    try {
      await axios.put(`${API}/${endpoint}/${leadId}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Modifiche salvate");
      onClose();
    } catch (err) {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/${endpoint}/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Scheda eliminata");
      onClose();
    } catch (err) {
      toast.error("Errore durante l'eliminazione");
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-body" data-testid="lead-detail-page">
      <div className="w-full max-w-3xl mx-auto relative">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200 flex items-center justify-between gap-2 px-4 py-3">
          <button onClick={onClose} data-testid="lead-detail-back-btn" className="p-2 rounded-full hover:bg-gray-100 text-buscema-green shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 data-testid="lead-detail-title" className="font-heading text-lg font-semibold text-buscema-green truncate">
              {loading ? "Caricamento..." : (form && config.titleOf(form)) || "Scheda contatto"}
            </h1>
            {form?.created_at && (
              <p className="text-xs text-gray-400">
                Inserita il {new Date(form.created_at).toLocaleString("it-IT")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!loading && form && (
              <DeleteLeadDialog
                name={config.titleOf(form)}
                onConfirm={handleDelete}
                deleting={deleting}
              >
                <button data-testid="lead-detail-delete-btn" className="p-2 rounded-full hover:bg-red-50 text-red-500">
                  <Trash2 className="w-5 h-5" />
                </button>
              </DeleteLeadDialog>
            )}
          </div>
        </header>

        {loading || !form ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-buscema-green" /></div>
        ) : (
          <>
            <main className="px-4 md:px-8 py-6 pb-32 space-y-6">
              {form.card_photo && (
                <img
                  src={form.card_photo}
                  alt="Biglietto da visita"
                  className="rounded-xl border border-gray-200 max-h-56 object-contain bg-white w-full"
                  data-testid="detail-card-photo"
                />
              )}

              {config.render({ form, set, toggle, inArr })}
            </main>

            {/* Sticky Save */}
            <div className="fixed bottom-0 inset-x-0 z-10 bg-white/95 backdrop-blur border-t border-gray-200 p-4">
              <div className="max-w-3xl mx-auto flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  data-testid="lead-detail-cancel-btn"
                  className="min-h-[56px] rounded-xl border-2 border-gray-300 text-gray-600 px-6"
                >
                  Annulla
                </Button>
                <Button
                  data-testid="lead-detail-save-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 min-h-[56px] rounded-xl bg-buscema-green hover:bg-buscema-greenDark text-white text-lg font-semibold gap-3 shadow-lg"
                >
                  {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Save className="w-6 h-6" />}
                  Salva Modifiche
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
